import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { supabase } from '../lib/supabase';
import { MagnifyingGlassIcon, ArrowPathIcon, HomeIcon } from '@heroicons/react/24/outline';
import {
  SearchResults,
  UnifiedSearchResponse,
  SearchProgramResult,
  SearchNoticeResult,
  SearchGalleryResult,
} from '../types';

type TabType = 'all' | 'programs' | 'notices' | 'galleries';

// 카테고리 코드를 한글로 변환
const categoryLabels: Record<string, string> = {
  group: '단체체험',
  preschool: '유아',
  elementary: '초등',
  secondary: '중등',
  adult: '성인',
  special: '특별',
  camp: '캠프',
  international: '국제교류',
};

// 검색어 하이라이트 함수
const highlightText = (text: string, query: string) => {
  if (!query || !text) return text;
  const regex = new RegExp(`(${query})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, index) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <span key={index} className="text-green-600 font-semibold underline decoration-green-400/50">
        {part}
      </span>
    ) : (
      part
    )
  );
};

const Search: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // URL의 q 파라미터가 변경되면 검색 실행
  useEffect(() => {
    const query = searchParams.get('q');
    if (query && query.trim().length >= 2) {
      setSearchQuery(query);
      performSearch(query);
    }
  }, [searchParams]);

  const performSearch = async (query: string) => {
    if (!query || query.trim().length < 2) {
      setError('검색어는 2자 이상 입력해주세요.');
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      console.log('Searching for:', query.trim());
      const { data, error: fnError } = await supabase.functions.invoke<UnifiedSearchResponse>('unified-search', {
        body: { query: query.trim(), category: 'all', limit: 50 },
      });

      console.log('Search response:', { data, error: fnError });

      if (fnError) {
        console.error('Search error:', fnError);
        setError('검색 중 오류가 발생했습니다.');
        return;
      }

      if (!data?.success) {
        console.error('Search failed:', data);
        setError(data?.message || '검색에 실패했습니다.');
        return;
      }

      console.log('Search results:', data.results);
      setResults(data.results || null);
    } catch (err) {
      console.error('Search error:', err);
      setError('검색 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim().length >= 2) {
      setSearchParams({ q: searchQuery.trim() });
    }
  };

  const handleReset = () => {
    setSearchQuery('');
    setSearchParams({});
    setResults(null);
    setHasSearched(false);
    setActiveTab('all');
  };

  // 페이지 카운트 제외한 총 개수
  const totalWithoutPages = (results?.counts.programs || 0) + (results?.counts.notices || 0) + (results?.counts.galleries || 0);

  const tabs = [
    { id: 'all', label: '전체', count: totalWithoutPages },
    { id: 'programs', label: '프로그램', count: results?.counts.programs || 0 },
    { id: 'notices', label: '공지사항', count: results?.counts.notices || 0 },
    { id: 'galleries', label: '갤러리', count: results?.counts.galleries || 0 },
  ];

  // 현재 탭에 따른 결과 개수
  const getCurrentTabCount = () => {
    if (!results) return 0;
    switch (activeTab) {
      case 'programs':
        return results.counts.programs;
      case 'notices':
        return results.counts.notices;
      case 'galleries':
        return results.counts.galleries;
      default:
        return totalWithoutPages;
    }
  };

  const renderProgramItem = (program: SearchProgramResult) => (
    <article key={program.id} className="border-b border-gray-200 py-5 last:border-b-0">
      <Link to={`/programs/${program.id}`} className="block group">
        <div className="flex gap-4">
          {/* 썸네일 이미지 */}
          {program.image_url && (
            <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-gray-100">
              <img
                src={program.image_url}
                alt={program.name}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-gray-900 group-hover:text-green-600 transition-colors mb-2">
              {highlightText(program.name, searchParams.get('q') || '')}
            </h3>
            {program.description && (
              <p className="text-gray-600 text-sm leading-relaxed mb-3 line-clamp-2">
                {highlightText(program.description, searchParams.get('q') || '')}
              </p>
            )}
            <div className="flex items-center text-sm text-gray-400">
              <HomeIcon className="w-4 h-4 mr-1" />
              <span>교육프로그램</span>
              {program.category && program.category.length > 0 && (
                <>
                  <span className="mx-1">&gt;</span>
                  <span>{categoryLabels[program.category[0]] || program.category[0]}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </Link>
    </article>
  );

  const renderNoticeItem = (notice: SearchNoticeResult) => (
    <article key={notice.id} className="border-b border-gray-200 py-5 last:border-b-0">
      <Link to={`/notice/${notice.id}`} className="block group">
        <h3 className="text-base font-bold text-gray-900 group-hover:text-green-600 transition-colors mb-2">
          {highlightText(notice.title, searchParams.get('q') || '')}
        </h3>
        {notice.content && (
          <p className="text-gray-600 text-sm leading-relaxed mb-3 line-clamp-2">
            {highlightText(notice.content, searchParams.get('q') || '')}
          </p>
        )}
        <div className="flex items-center text-sm text-gray-400">
          <HomeIcon className="w-4 h-4 mr-1" />
          <span>커뮤니티</span>
          <span className="mx-1">&gt;</span>
          <span>공지사항</span>
        </div>
      </Link>
    </article>
  );

  const renderGalleryItem = (gallery: SearchGalleryResult) => (
    <article key={gallery.id} className="border-b border-gray-200 py-5 last:border-b-0">
      <Link to={`/gallery/${gallery.id}`} className="block group">
        <div className="flex gap-4">
          {/* 썸네일 이미지 */}
          {gallery.image_url && (
            <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-gray-100">
              <img
                src={gallery.image_url}
                alt={gallery.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-gray-900 group-hover:text-green-600 transition-colors mb-2">
              {highlightText(gallery.title, searchParams.get('q') || '')}
            </h3>
            {gallery.description && (
              <p className="text-gray-600 text-sm leading-relaxed mb-3 line-clamp-2">
                {highlightText(gallery.description, searchParams.get('q') || '')}
              </p>
            )}
            <div className="flex items-center text-sm text-gray-400">
              <HomeIcon className="w-4 h-4 mr-1" />
              <span>커뮤니티</span>
              <span className="mx-1">&gt;</span>
              <span>갤러리</span>
            </div>
          </div>
        </div>
      </Link>
    </article>
  );

  const renderResults = () => {
    if (!results) return null;

    const showPrograms = activeTab === 'all' || activeTab === 'programs';
    const showNotices = activeTab === 'all' || activeTab === 'notices';
    const showGalleries = activeTab === 'all' || activeTab === 'galleries';

    const hasResults =
      (showPrograms && results.programs.length > 0) ||
      (showNotices && results.notices.length > 0) ||
      (showGalleries && results.galleries.length > 0);

    if (!hasResults) {
      return (
        <div className="text-center py-20">
          <div className="text-gray-300 mb-4">
            <MagnifyingGlassIcon className="h-16 w-16 mx-auto" />
          </div>
          <p className="text-gray-500 text-lg">검색 결과가 없습니다.</p>
          <p className="text-gray-400 text-sm mt-2">다른 검색어로 다시 시도해 보세요.</p>
        </div>
      );
    }

    return (
      <div className="space-y-8">
        {/* 프로그램 */}
        {showPrograms && results.programs.length > 0 && (
          <section className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              프로그램 <span className="text-green-600">({results.programs.length})</span>
            </h2>
            <div className="border-t border-gray-200">
              {(activeTab === 'all' ? results.programs.slice(0, 5) : results.programs).map(renderProgramItem)}
            </div>
          </section>
        )}

        {/* 공지사항 */}
        {showNotices && results.notices.length > 0 && (
          <section className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              공지사항 <span className="text-green-600">({results.notices.length})</span>
            </h2>
            <div className="border-t border-gray-200">
              {(activeTab === 'all' ? results.notices.slice(0, 5) : results.notices).map(renderNoticeItem)}
            </div>
          </section>
        )}

        {/* 갤러리 */}
        {showGalleries && results.galleries.length > 0 && (
          <section className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              갤러리 <span className="text-green-600">({results.galleries.length})</span>
            </h2>
            <div className="border-t border-gray-200">
              {(activeTab === 'all' ? results.galleries.slice(0, 5) : results.galleries).map(renderGalleryItem)}
            </div>
          </section>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 mt-20">
        {/* Hero Section - 깔끔한 텍스트 중앙 정렬 */}
        <section className="bg-white py-12 sm:py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
              통합검색
            </h1>
          </div>
        </section>

        {/* Search Box Section - 밝은 회색 배경 */}
        <section className="bg-gray-100 border-y border-gray-200 py-8 sm:py-10">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">
            <form onSubmit={handleSearch} className="flex shadow-sm">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="검색어를 입력하세요"
                className="flex-1 px-5 py-4 text-base border border-gray-200 border-r-0 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
              />
              <button
                type="submit"
                disabled={loading || searchQuery.trim().length < 2}
                className="px-5 py-4 bg-green-600 text-white rounded-r-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                <MagnifyingGlassIcon className="w-5 h-5" />
              </button>
            </form>
          </div>
        </section>

        {/* Results Section - 밝은 배경 */}
        <section className="bg-gray-50 py-8 sm:py-10 md:py-12 min-h-[400px]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
                {error}
              </div>
            )}

            {/* Results Count & Reset */}
            {hasSearched && results && (
              <div className="flex items-center justify-between mb-6">
                <p className="text-gray-700">
                  총 <span className="text-green-600 font-bold">{getCurrentTabCount()}</span>건
                </p>
                <button
                  onClick={handleReset}
                  className="flex items-center text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <ArrowPathIcon className="w-4 h-4 mr-1" />
                  검색초기화
                </button>
              </div>
            )}

            {/* Tabs */}
            {hasSearched && results && (
              <div className="mb-8">
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as TabType)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        activeTab === tab.id
                          ? 'bg-green-600 text-white shadow-md'
                          : 'bg-white text-gray-700 hover:bg-gray-50 hover:shadow-sm border border-gray-200'
                      }`}
                    >
                      {tab.label}
                      <span className={activeTab === tab.id ? 'text-green-100 ml-1' : 'text-gray-400 ml-1'}>
                        ({tab.count})
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Results */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
              </div>
            ) : hasSearched ? (
              renderResults()
            ) : (
              <div className="text-center py-20">
                <div className="text-gray-200 mb-4">
                  <MagnifyingGlassIcon className="h-24 w-24 mx-auto" />
                </div>
                <p className="text-gray-500 text-lg">검색어를 입력해주세요.</p>
                <p className="text-gray-400 text-sm mt-2">
                  프로그램명, 강사명, 공지사항 제목 등으로 검색할 수 있습니다.
                </p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Search;
